import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import {
    createVatRate,
    updateVatRate,
} from '@/modules/Settings/services/product-group-vat'
import {
    CreateProductGroupVatRatePayload,
    ProductGroupVatRate,
} from '@/modules/Settings/types/product-group-vat'
import { ModalProps } from '@/types/shared'
import dayjs from 'dayjs'
import { Loader2, Save } from 'lucide-react'
import React from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Button, Dialog, FormItem } from 'vnetwork-ui'

export interface CreateEditVatRateModalProps extends ModalProps {
    productGroupCode: string
    vatRate?: ProductGroupVatRate | null
}

const CreateEditVatRateModal: React.FC<CreateEditVatRateModalProps> = ({
    productGroupCode,
    vatRate,
    onClose,
    open,
}) => {
    const { t } = useTranslation('settings')
    const isEdit = !!vatRate

    const { control, handleSubmit } = useForm<CreateProductGroupVatRatePayload>(
        {
            defaultValues: vatRate
                ? {
                      tax_rate: vatRate.tax_rate,
                      effective_from: dayjs(vatRate.effective_from).format(
                          'YYYY-MM-DD'
                      ),
                      effective_to: vatRate.effective_to
                          ? dayjs(vatRate.effective_to).format('YYYY-MM-DD')
                          : undefined,
                      description: vatRate.description || '',
                  }
                : {
                      tax_rate: 0.1,
                      effective_from: dayjs().format('YYYY-MM-DD'),
                      effective_to: undefined,
                      description: '',
                  },
        }
    )

    const { mutateAsync, isPending } = useMutationWithHandler(
        {
            mutationFn: async (data: CreateProductGroupVatRatePayload) => {
                if (isEdit && vatRate) {
                    return updateVatRate(productGroupCode, vatRate.id, data)
                }
                return createVatRate(productGroupCode, data)
            },
        },
        {
            successMessage: isEdit
                ? t('vat-rate-updated-successfully')
                : t('vat-rate-created-successfully'),
            reactQueryKeys: [QUERY_KEYS.PRODUCT_GROUP_VAT_RATES],
            onSuccess: () => onClose(),
        }
    )

    return (
        <Dialog
            open={open}
            onClose={onClose}
            title={isEdit ? t('edit-vat-rate') : t('create-vat-rate')}
            maxWidth="sm"
        >
            <form
                onSubmit={handleSubmit((data) =>
                    mutateAsync({ ...data, tax_rate: Number(data.tax_rate) })
                )}
                className="space-y-4"
            >
                <FormItem
                    name="tax_rate"
                    type="textfield"
                    control={control}
                    label={t('tax-rate')}
                    placeholder="0.10"
                    rules={{
                        required: t('field-required'),
                        validate: (value: number) => {
                            const num = Number(value)
                            if (isNaN(num) || num < 0 || num > 1) {
                                return t('tax-rate-must-be-between-0-and-1')
                            }
                            return true
                        },
                    }}
                />
                <FormItem
                    name="effective_from"
                    type="textfield"
                    control={control}
                    label={t('effective-from')}
                    placeholder="YYYY-MM-DD"
                    rules={{ required: t('field-required') }}
                />
                <FormItem
                    name="effective_to"
                    type="textfield"
                    control={control}
                    label={t('effective-to')}
                    placeholder={t('effective-to-placeholder')}
                />
                <FormItem
                    name="description"
                    type="textarea"
                    control={control}
                    label={t('description')}
                    placeholder={t('vat-rate-description-placeholder')}
                />

                <div className="flex items-center justify-end gap-3 pt-2">
                    <Button
                        type="button"
                        variant="outlined"
                        onClick={onClose}
                        disabled={isPending}
                    >
                        {t('cancel')}
                    </Button>
                    <Button
                        type="submit"
                        disabled={isPending}
                        variant="contained"
                    >
                        {isPending ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                            <Save className="h-4 w-4" />
                        )}
                        {isEdit ? t('update') : t('create')}
                    </Button>
                </div>
            </form>
        </Dialog>
    )
}

export default CreateEditVatRateModal
